import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const formVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const inputVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  return (
    <section id="contact" className="py-20 bg-[#F5F5F5]" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4">
            Contactez-<span className="text-[#FFD700]">nous</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Une question, une suggestion ou besoin d'informations ? N'hésitez pas à nous contacter !
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.6 }}
            className="bg-white p-8 rounded-xl shadow-lg relative overflow-hidden"
          >
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#87CEFA] opacity-10 rounded-full"></div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#FFD700] opacity-10 rounded-full"></div>
            
            <h3 className="text-2xl font-bold text-[#1C1C1C] mb-6 relative">Nos informations</h3>
            
            <div className="space-y-6 relative">
              <div className="flex items-start">
                <div className="bg-[#87CEFA] bg-opacity-20 p-3 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#87CEFA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1C1C1C]">Adresse</h4>
                  <p className="text-gray-600">123 Avenue des Enfants Heureux, 75000 Paris</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-[#87CEFA] bg-opacity-20 p-3 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#87CEFA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1C1C1C]">Téléphone</h4>
                  <p className="text-gray-600">01 23 45 67 89</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-[#87CEFA] bg-opacity-20 p-3 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#87CEFA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1C1C1C]">Email</h4>
                  <p className="text-gray-600">contact@eveiljeunesse.fr</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-[#87CEFA] bg-opacity-20 p-3 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#87CEFA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1C1C1C]">Horaires</h4>
                  <p className="text-gray-600">Lundi - Vendredi: 9h - 18h</p>
                  <p className="text-gray-600">Samedi: 10h - 14h</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            variants={formVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="bg-white p-8 rounded-xl shadow-lg"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <motion.div custom={0} variants={inputVariants}>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <input
                  type="text"
                  id="firstName"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEFA] focus:border-transparent outline-none transition-colors"
                  placeholder="Votre prénom"
                />
              </motion.div>
              
              <motion.div custom={1} variants={inputVariants}>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  id="lastName"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEFA] focus:border-transparent outline-none transition-colors"
                  placeholder="Votre nom"
                />
              </motion.div>
            </div>
            
            <motion.div className="mb-6" custom={2} variants={inputVariants}>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                id="email"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEFA] focus:border-transparent outline-none transition-colors"
                placeholder="votre@email.com"
              />
            </motion.div>
            
            <motion.div className="mb-6" custom={3} variants={inputVariants}>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
              <select
                id="subject"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEFA] focus:border-transparent outline-none transition-colors"
              >
                <option value="" disabled selected>Choisissez un sujet</option>
                <option value="information">Demande d'informations</option>
                <option value="partnership">Partenariat</option>
                <option value="suggestion">Suggestion</option>
                <option value="other">Autre</option>
              </select>
            </motion.div>
            
            <motion.div className="mb-6" custom={4} variants={inputVariants}>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                id="message"
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEFA] focus:border-transparent outline-none transition-colors resize-none"
                placeholder="Votre message"
              ></textarea>
            </motion.div>
            
            <motion.div
              className="flex items-center mb-6"
              custom={5}
              variants={inputVariants}
            >
              <input
                id="privacy"
                type="checkbox"
                className="h-4 w-4 text-[#87CEFA] focus:ring-[#87CEFA] border-gray-300 rounded"
              />
              <label htmlFor="privacy" className="ml-2 block text-sm text-gray-600">
                J'accepte la politique de confidentialité
              </label>
            </motion.div>
            
            <motion.button
              type="submit"
              className="w-full bg-[#FFD700] hover:bg-[#F8C400] text-[#1C1C1C] font-bold py-3 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              custom={6}
              variants={inputVariants}
            >
              Envoyer le message
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
